import { useLocation } from "react-router-dom";
import { useLanguage } from "@/hooks/useLanguage";
import { getTranslation, getBaseLanguage } from "@/lib/i18n";
import {
  BRAND_DESCRIPTION,
  BRAND_HANDLE,
  BRAND_NAME,
  BRAND_OG_IMAGE_URL,
  BRAND_SITE_NAME,
  BRAND_SITE_URL,
  getBrandDisplayName,
} from "@/lib/brandConfig";
import { BRAND_SEO_HOME_DESCRIPTION_ES, BRAND_SEO_HOME_TITLE_ES } from "@/lib/brandSeoCopy";

export const StructuredData = () => {
  const location = useLocation();
  const { currentLanguage } = useLanguage();
  const baseUrl = BRAND_SITE_URL.replace(/\/$/, "");
  const displayName = getBrandDisplayName();
  const cleanPath =
    location.pathname.endsWith("/") && location.pathname !== "/"
      ? location.pathname.slice(0, -1)
      : location.pathname;
  const currentUrl = `${baseUrl}${cleanPath || "/"}`;
  const isHome = cleanPath === "/" || cleanPath === "";

  const siteName =
    getTranslation(currentLanguage, "common.siteName")?.trim() ||
    BRAND_SITE_NAME?.trim() ||
    displayName;
  const description =
    getTranslation(currentLanguage, "common.description")?.trim() ||
    BRAND_DESCRIPTION?.trim() ||
    BRAND_SEO_HOME_DESCRIPTION_ES;

  const baseLang = getBaseLanguage(currentLanguage);
  const inLanguage = baseLang === "es" ? "es-AR" : "en";

  const personId = `${baseUrl}/#person`;
  const websiteId = `${baseUrl}/#website`;
  const pageId = `${currentUrl}#webpage`;

  const personEntity = {
    "@type": "Person",
    "@id": personId,
    name: displayName,
    alternateName: [BRAND_NAME, `@${BRAND_HANDLE}`].filter(Boolean),
    url: `${baseUrl}/`,
    description,
    image: BRAND_OG_IMAGE_URL || `${baseUrl}/og.png`,
    sameAs: [
      `https://www.instagram.com/${BRAND_HANDLE}/`,
      `https://www.tiktok.com/@${BRAND_HANDLE}`,
    ],
    jobTitle:
      baseLang === "es"
        ? "Modelo y creadora de contenido"
        : "Model and Content Creator",
    nationality: {
      "@type": "Country",
      name: "Argentina",
    },
    knowsAbout: [
      "Moda",
      "Lifestyle",
      "Fotografía",
      "Redes sociales",
      "Campañas",
    ],
  };

  const websiteEntity = {
    "@type": "WebSite",
    "@id": websiteId,
    url: `${baseUrl}/`,
    name: siteName,
    description: `Sitio oficial de ${displayName}.`,
    publisher: { "@id": personId },
    inLanguage: ["es-AR", "en"],
  };

  const getPageType = (pathname: string) => {
    if (pathname === "/") return "ProfilePage";
    if (
      pathname.includes("fotos") ||
      pathname.includes("imagenes") ||
      pathname.includes("galeria")
    ) {
      return "ImageGallery";
    }
    if (
      pathname.includes("videos") ||
      pathname.includes("reels") ||
      pathname.includes("video")
    ) {
      return "VideoGallery";
    }
    if (
      pathname.includes("content-creator") ||
      pathname.includes("creadora-de-contenido") ||
      pathname.includes("influencer")
    ) {
      return "AboutPage";
    }
    return "WebPage";
  };

  const pageType = getPageType(cleanPath);
  const pageName = isHome
    ? BRAND_SEO_HOME_TITLE_ES
    : typeof document !== "undefined" && document.title?.trim()
      ? document.title.trim()
      : siteName;

  const pageEntity: Record<string, unknown> = {
    "@type": pageType,
    "@id": pageId,
    name: pageName,
    url: currentUrl,
    description,
    dateModified: new Date().toISOString(),
    inLanguage,
    isPartOf: { "@id": websiteId },
  };

  // ProfilePage debe enlazar la persona con name (requisito GSC / Rich Results).
  if (isHome) {
    pageEntity.mainEntity = personEntity;
  } else {
    pageEntity.author = {
      "@type": "Person",
      "@id": personId,
      name: displayName,
    };
  }

  const breadcrumbSchema = {
    "@type": "BreadcrumbList",
    "@id": `${currentUrl}#breadcrumb`,
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: baseLang === "es" ? "Inicio" : "Home",
        item: `${baseUrl}/`,
      },
      ...(cleanPath !== "/"
        ? [
            {
              "@type": "ListItem",
              position: 2,
              name: pageName,
              item: currentUrl,
            },
          ]
        : []),
    ],
  };

  const graphSchema = {
    "@context": "https://schema.org",
    "@graph": [personEntity, websiteEntity, pageEntity, breadcrumbSchema],
  };

  return (
    <script
      type="application/ld+json"
      id="structured-data-graph"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(graphSchema) }}
    />
  );
};